/**
 * Mock Service Stores Index
 *
 * This module exports all mock service stores for development use.
 * These can be used when the backend API services are not yet available.
 *
 * Usage:
 * 1. Import individual mock stores for testing
 * 2. Use createMock*Store() functions to instantiate stores
 * 3. Replace with real ServiceStores when backend is ready
 */

// Base class
export { MockServiceStore } from "./MockServiceStore";

// Plants
import {
  mockPlants as _mockPlants,
  createMockPlantsStore as _createMockPlantsStore,
} from "./plants.mock";
export type { Plant } from "./plants.mock";
export const mockPlants = _mockPlants;
export const createMockPlantsStore = _createMockPlantsStore;

// Purchase Orders
import {
  mockPurchaseOrders as _mockPurchaseOrders,
  createMockPurchaseOrdersStore as _createMockPurchaseOrdersStore,
} from "./purchase-orders.mock";
export type { PurchaseOrder, POStatus, POPriority, FreightType } from "./purchase-orders.mock";
export const mockPurchaseOrders = _mockPurchaseOrders;
export const createMockPurchaseOrdersStore = _createMockPurchaseOrdersStore;

// Purchase Order Line Items
import {
  mockPurchaseOrderLineItems as _mockPurchaseOrderLineItems,
  createMockPurchaseOrderLineItemsStore as _createMockPurchaseOrderLineItemsStore,
} from "./purchase-order-line-items.mock";
export type { PurchaseOrderLineItem, LineItemStatus } from "./purchase-order-line-items.mock";
export const mockPurchaseOrderLineItems = _mockPurchaseOrderLineItems;
export const createMockPurchaseOrderLineItemsStore = _createMockPurchaseOrderLineItemsStore;

// Approval Queue
import {
  mockApprovalQueueTasks as _mockApprovalQueueTasks,
  createMockApprovalQueueStore as _createMockApprovalQueueStore,
} from "./approval-queue.mock";
export type { ApprovalQueueTask, QueueType, TaskStatus, TaskPriority } from "./approval-queue.mock";
export const mockApprovalQueueTasks = _mockApprovalQueueTasks;
export const createMockApprovalQueueStore = _createMockApprovalQueueStore;

// GL Accounts
import {
  mockGLAccounts as _mockGLAccounts,
  createMockGLAccountsStore as _createMockGLAccountsStore,
} from "./gl-accounts.mock";
export type { GLAccount, AccountType } from "./gl-accounts.mock";
export const mockGLAccounts = _mockGLAccounts;
export const createMockGLAccountsStore = _createMockGLAccountsStore;

// Freight Carriers
import {
  mockFreightCarriers as _mockFreightCarriers,
  createMockFreightCarriersStore as _createMockFreightCarriersStore,
} from "./freight-carriers.mock";
export type { FreightCarrier } from "./freight-carriers.mock";
export const mockFreightCarriers = _mockFreightCarriers;
export const createMockFreightCarriersStore = _createMockFreightCarriersStore;

// StructureCast Clients
import {
  mockStructureCastClients as _mockStructureCastClients,
  createMockStructureCastClientStore as _createMockStructureCastClientStore,
} from "./structurecast-clients.mock";
export type { StructureCastClient } from "./structurecast-clients.mock";
export const mockStructureCastClients = _mockStructureCastClients;
export const createMockStructureCastClientStore = _createMockStructureCastClientStore;

// StructureCast Projects
import {
  mockStructureCastProjects as _mockStructureCastProjects,
  createMockStructureCastProjectStore as _createMockStructureCastProjectStore,
} from "./structurecast-projects.mock";
export type { StructureCastProject } from "./structurecast-projects.mock";
export const mockStructureCastProjects = _mockStructureCastProjects;
export const createMockStructureCastProjectStore = _createMockStructureCastProjectStore;

// StructureCast Structures
import {
  mockStructureCastStructures as _mockStructureCastStructures,
  createMockStructureCastStructureStore as _createMockStructureCastStructureStore,
} from "./structurecast-structures.mock";
export type { StructureCastStructure } from "./structurecast-structures.mock";
export const mockStructureCastStructures = _mockStructureCastStructures;
export const createMockStructureCastStructureStore = _createMockStructureCastStructureStore;

// StructureCast Components
import {
  mockStructureCastComponents as _mockStructureCastComponents,
  createMockStructureCastComponentStore as _createMockStructureCastComponentStore,
} from "./structurecast-components.mock";
export type { StructureCastComponent } from "./structurecast-components.mock";
export const mockStructureCastComponents = _mockStructureCastComponents;
export const createMockStructureCastComponentStore = _createMockStructureCastComponentStore;

// StructureCast Casting Groups
import {
  mockStructureCastCastingGroups as _mockStructureCastCastingGroups,
  createMockStructureCastCastingGroupStore as _createMockStructureCastCastingGroupStore,
} from "./structurecast-casting-groups.mock";
export type { StructureCastCastingGroup } from "./structurecast-casting-groups.mock";
export const mockStructureCastCastingGroups = _mockStructureCastCastingGroups;
export const createMockStructureCastCastingGroupStore = _createMockStructureCastCastingGroupStore;

// StructureCast Casting Days
import {
  mockStructureCastCastingDays as _mockStructureCastCastingDays,
  createMockStructureCastCastingDayStore as _createMockStructureCastCastingDayStore,
} from "./structurecast-casting-days.mock";
export type { StructureCastCastingDay } from "./structurecast-casting-days.mock";
export const mockStructureCastCastingDays = _mockStructureCastCastingDays;
export const createMockStructureCastCastingDayStore = _createMockStructureCastCastingDayStore;

// StructureCast Mold Assignments
import {
  mockStructureCastMoldAssignments as _mockStructureCastMoldAssignments,
  createMockStructureCastMoldAssignmentStore as _createMockStructureCastMoldAssignmentStore,
} from "./structurecast-mold-assignments.mock";
export type { StructureCastMoldAssignment } from "./structurecast-mold-assignments.mock";
export const mockStructureCastMoldAssignments = _mockStructureCastMoldAssignments;
export const createMockStructureCastMoldAssignmentStore = _createMockStructureCastMoldAssignmentStore;

// StructureCast Shipments
import {
  mockStructureCastShipments as _mockStructureCastShipments,
  createMockStructureCastShipmentStore as _createMockStructureCastShipmentStore,
} from "./structurecast-shipments.mock";
export type { StructureCastShipment } from "./structurecast-shipments.mock";
export const mockStructureCastShipments = _mockStructureCastShipments;
export const createMockStructureCastShipmentStore = _createMockStructureCastShipmentStore;

// StructureCast Shipment Loads
import {
  mockStructureCastShipmentLoads as _mockStructureCastShipmentLoads,
  createMockStructureCastShipmentLoadStore as _createMockStructureCastShipmentLoadStore,
} from "./structurecast-shipment-loads.mock";
export type { StructureCastShipmentLoad } from "./structurecast-shipment-loads.mock";
export const mockStructureCastShipmentLoads = _mockStructureCastShipmentLoads;
export const createMockStructureCastShipmentLoadStore = _createMockStructureCastShipmentLoadStore;

// StructureCast Casting Tables
import {
  mockStructureCastCastingTables as _mockStructureCastCastingTables,
  createMockStructureCastCastingTableStore as _createMockStructureCastCastingTableStore,
} from "./structurecast-casting-tables.mock";
export type { StructureCastCastingTable } from "./structurecast-casting-tables.mock";
export const mockStructureCastCastingTables = _mockStructureCastCastingTables;
export const createMockStructureCastCastingTableStore = _createMockStructureCastCastingTableStore;

// StructureCast Pre-Production Meetings
import {
  mockStructureCastPreProductionMeetings as _mockStructureCastPreProductionMeetings,
  createMockStructureCastPreProductionMeetingStore as _createMockStructureCastPreProductionMeetingStore,
} from "./structurecast-pre-production-meetings.mock";
export type { StructureCastPreProductionMeeting } from "./structurecast-pre-production-meetings.mock";
export const mockStructureCastPreProductionMeetings = _mockStructureCastPreProductionMeetings;
export const createMockStructureCastPreProductionMeetingStore = _createMockStructureCastPreProductionMeetingStore;

// StructureCast Yard Zones
import {
  mockStructureCastYardZones as _mockStructureCastYardZones,
  createMockStructureCastYardZoneStore as _createMockStructureCastYardZoneStore,
} from "./structurecast-yard-zones.mock";
export type { StructureCastYardZone } from "./structurecast-yard-zones.mock";
export const mockStructureCastYardZones = _mockStructureCastYardZones;
export const createMockStructureCastYardZoneStore = _createMockStructureCastYardZoneStore;

// StructureCast Estimates
import {
  mockStructureCastEstimates as _mockStructureCastEstimates,
  createMockStructureCastEstimateStore as _createMockStructureCastEstimateStore,
} from "./structurecast-estimates.mock";
export type { StructureCastEstimate } from "./structurecast-estimates.mock";
export const mockStructureCastEstimates = _mockStructureCastEstimates;
export const createMockStructureCastEstimateStore = _createMockStructureCastEstimateStore;

// StructureCast Estimate Line Items
import {
  mockStructureCastEstimateLineItems as _mockStructureCastEstimateLineItems,
  createMockStructureCastEstimateLineItemStore as _createMockStructureCastEstimateLineItemStore,
} from "./structurecast-estimate-line-items.mock";
export type { StructureCastEstimateLineItem, EstimateComponentDefinition } from "./structurecast-estimate-line-items.mock";
export const mockStructureCastEstimateLineItems = _mockStructureCastEstimateLineItems;
export const createMockStructureCastEstimateLineItemStore = _createMockStructureCastEstimateLineItemStore;

// StructureCast QC Inspections
import {
  mockStructureCastQcInspections as _mockStructureCastQcInspections,
  createMockStructureCastQcInspectionStore as _createMockStructureCastQcInspectionStore,
} from "./structurecast-qc-inspections.mock";
export type { StructureCastQcInspection, QcSection, QcCheckItem } from "./structurecast-qc-inspections.mock";
export const mockStructureCastQcInspections = _mockStructureCastQcInspections;
export const createMockStructureCastQcInspectionStore = _createMockStructureCastQcInspectionStore;

// StructureCast Batch Tickets
import {
  mockStructureCastBatchTickets as _mockStructureCastBatchTickets,
  createMockStructureCastBatchTicketStore as _createMockStructureCastBatchTicketStore,
} from "./structurecast-batch-tickets.mock";
export type { StructureCastBatchTicket } from "./structurecast-batch-tickets.mock";
export const mockStructureCastBatchTickets = _mockStructureCastBatchTickets;
export const createMockStructureCastBatchTicketStore = _createMockStructureCastBatchTicketStore;

// StructureCast Raw Materials
import {
  mockStructureCastRawMaterials as _mockStructureCastRawMaterials,
  createMockStructureCastRawMaterialStore as _createMockStructureCastRawMaterialStore,
} from "./structurecast-raw-materials.mock";
export type { StructureCastRawMaterial } from "./structurecast-raw-materials.mock";
export const mockStructureCastRawMaterials = _mockStructureCastRawMaterials;
export const createMockStructureCastRawMaterialStore = _createMockStructureCastRawMaterialStore;

// StructureCast Vendors
import {
  mockStructureCastVendors as _mockStructureCastVendors,
  createMockStructureCastVendorStore as _createMockStructureCastVendorStore,
} from "./structurecast-vendors.mock";
export type { StructureCastVendor } from "./structurecast-vendors.mock";
export const mockStructureCastVendors = _mockStructureCastVendors;
export const createMockStructureCastVendorStore = _createMockStructureCastVendorStore;

// StructureCast Purchase Orders
import {
  mockStructureCastPurchaseOrders as _mockStructureCastPurchaseOrders,
  createMockStructureCastPurchaseOrderStore as _createMockStructureCastPurchaseOrderStore,
} from "./structurecast-purchase-orders.mock";
export type { StructureCastPurchaseOrder, StructureCastPOStatus } from "./structurecast-purchase-orders.mock";
export const mockStructureCastPurchaseOrders = _mockStructureCastPurchaseOrders;
export const createMockStructureCastPurchaseOrderStore = _createMockStructureCastPurchaseOrderStore;

// StructureCast Purchase Order Line Items
import {
  mockStructureCastPurchaseOrderLineItems as _mockStructureCastPurchaseOrderLineItems,
  createMockStructureCastPurchaseOrderLineItemStore as _createMockStructureCastPurchaseOrderLineItemStore,
} from "./structurecast-purchase-order-line-items.mock";
export type { StructureCastPurchaseOrderLineItem } from "./structurecast-purchase-order-line-items.mock";
export const mockStructureCastPurchaseOrderLineItems = _mockStructureCastPurchaseOrderLineItems;
export const createMockStructureCastPurchaseOrderLineItemStore = _createMockStructureCastPurchaseOrderLineItemStore;

/**
 * Initialize all mock stores
 *
 * Use this function to create all mock stores at once.
 * Useful for development and testing environments.
 *
 * @returns Object containing all mock store instances
 */
export const initializeMockStores = () => {
  return {
    plantsStore: createMockPlantsStore(),
    purchaseOrdersStore: createMockPurchaseOrdersStore(),
    purchaseOrderLineItemsStore: createMockPurchaseOrderLineItemsStore(),
    approvalQueueStore: createMockApprovalQueueStore(),
    glAccountsStore: createMockGLAccountsStore(),
    freightCarriersStore: createMockFreightCarriersStore(),
    structureCastClientsStore: createMockStructureCastClientStore(),
    structureCastProjectsStore: createMockStructureCastProjectStore(),
    structureCastStructuresStore: createMockStructureCastStructureStore(),
    structureCastComponentsStore: createMockStructureCastComponentStore(),
    structureCastCastingGroupsStore: createMockStructureCastCastingGroupStore(),
    structureCastCastingDaysStore: createMockStructureCastCastingDayStore(),
    structureCastMoldAssignmentsStore: createMockStructureCastMoldAssignmentStore(),
    structureCastShipmentsStore: createMockStructureCastShipmentStore(),
    structureCastShipmentLoadsStore: createMockStructureCastShipmentLoadStore(),
    structureCastCastingTablesStore: createMockStructureCastCastingTableStore(),
    structureCastPreProductionMeetingsStore: createMockStructureCastPreProductionMeetingStore(),
    structureCastYardZonesStore: createMockStructureCastYardZoneStore(),
    structureCastEstimatesStore: createMockStructureCastEstimateStore(),
    structureCastEstimateLineItemsStore: createMockStructureCastEstimateLineItemStore(),
    structureCastQcInspectionsStore: createMockStructureCastQcInspectionStore(),
    structureCastBatchTicketsStore: createMockStructureCastBatchTicketStore(),
    structureCastRawMaterialsStore: createMockStructureCastRawMaterialStore(),
    structureCastVendorsStore: createMockStructureCastVendorStore(),
    structureCastPurchaseOrdersStore: createMockStructureCastPurchaseOrderStore(),
    structureCastPurchaseOrderLineItemsStore: createMockStructureCastPurchaseOrderLineItemStore(),
  };
};

/**
 * Mock store configuration for integration with serviceStoresConfig
 *
 * Note: These are placeholder configurations. The actual integration
 * depends on how the project's ServiceStore system is structured.
 */
export const mockStoreConfigs = [
  {
    name: "plants",
    mock: true,
    createStore: createMockPlantsStore,
  },
  {
    name: "purchase-orders",
    mock: true,
    createStore: createMockPurchaseOrdersStore,
  },
  {
    name: "purchase-order-line-items",
    mock: true,
    createStore: createMockPurchaseOrderLineItemsStore,
  },
  {
    name: "approval-queue",
    mock: true,
    createStore: createMockApprovalQueueStore,
  },
  {
    name: "gl-accounts",
    mock: true,
    createStore: createMockGLAccountsStore,
  },
  {
    name: "freight-carriers",
    mock: true,
    createStore: createMockFreightCarriersStore,
  },
  {
    name: "structurecast-clients",
    mock: true,
    createStore: createMockStructureCastClientStore,
  },
  {
    name: "structurecast-projects",
    mock: true,
    createStore: createMockStructureCastProjectStore,
  },
  {
    name: "structurecast-structures",
    mock: true,
    createStore: createMockStructureCastStructureStore,
  },
  {
    name: "structurecast-components",
    mock: true,
    createStore: createMockStructureCastComponentStore,
  },
  {
    name: "structurecast-casting-groups",
    mock: true,
    createStore: createMockStructureCastCastingGroupStore,
  },
  {
    name: "structurecast-casting-days",
    mock: true,
    createStore: createMockStructureCastCastingDayStore,
  },
  {
    name: "structurecast-mold-assignments",
    mock: true,
    createStore: createMockStructureCastMoldAssignmentStore,
  },
  {
    name: "structurecast-shipments",
    mock: true,
    createStore: createMockStructureCastShipmentStore,
  },
  {
    name: "structurecast-shipment-loads",
    mock: true,
    createStore: createMockStructureCastShipmentLoadStore,
  },
  {
    name: "structurecast-casting-tables",
    mock: true,
    createStore: createMockStructureCastCastingTableStore,
  },
  {
    name: "structurecast-pre-production-meetings",
    mock: true,
    createStore: createMockStructureCastPreProductionMeetingStore,
  },
  {
    name: "structurecast-yard-zones",
    mock: true,
    createStore: createMockStructureCastYardZoneStore,
  },
  {
    name: "structurecast-estimates",
    mock: true,
    createStore: createMockStructureCastEstimateStore,
  },
  {
    name: "structurecast-estimate-line-items",
    mock: true,
    createStore: createMockStructureCastEstimateLineItemStore,
  },
  {
    name: "structurecast-qc-inspections",
    mock: true,
    createStore: createMockStructureCastQcInspectionStore,
  },
  {
    name: "structurecast-batch-tickets",
    mock: true,
    createStore: createMockStructureCastBatchTicketStore,
  },
  {
    name: "structurecast-raw-materials",
    mock: true,
    createStore: createMockStructureCastRawMaterialStore,
  },
  {
    name: "structurecast-vendors",
    mock: true,
    createStore: createMockStructureCastVendorStore,
  },
  {
    name: "structurecast-purchase-orders",
    mock: true,
    createStore: createMockStructureCastPurchaseOrderStore,
  },
  {
    name: "structurecast-purchase-order-line-items",
    mock: true,
    createStore: createMockStructureCastPurchaseOrderLineItemStore,
  },
];

export default mockStoreConfigs;
